import React from "react";
import ReactDOM from "react-dom/client";

/** @typedef {import('../../models/CfcRegistrationModel.js').CfcRegistrationItem } CfcRegistrationItem */
/** @typedef {import('../../models/CfcCategoryModel.js').CfcCategoryItem } CfcCategoryItem */

/**
 * @typedef {object} CfcRegistrationsProps
 * @property {?string} id - ID du tournoi pour lequel on veut récupérer les inscriptions
 * @property {Array<CfcRegistrationItem>} cfcRegistrations - Liste des inscriptions
 * @property {Array<CfcCategoryItem>} cfcCategories - Liste des catégories
 * @property {Array<CfcRegistrationItem>} filteredCfcRegistrations - Liste des inscriptions filtrées
 * @property {{ cfcCategoryId: ?number, searchValue: ?string }} search - Objet contenant les valeurs de recherche
 */

class CfcRegistrations extends React.Component {
  /**
   * @param {CfcRegistrationsProps} props
   */
  constructor(props) {
    super(props);
    this.state = {
      id: props.id || null,
      cfcRegistrations: /** @type {Array<CfcRegistrationItem>} */ ([]),
      filteredCfcRegistrations: /** @type {Array<CfcRegistrationItem>} */ ([]),
      cfcCategories: /** @type {Array<CfcCategoryItem>} */ ([]),
      search: {
        cfcCategoryId: null,
        searchValue: null
      }
    };
  }

  async componentDidMount() {
    const cfcRegistrations = await this.fetchCfcRegistrations();
    const cfcCategories = await this.fetchCfcCategories();

    this.setState({ cfcRegistrations, cfcCategories, filteredCfcRegistrations: cfcRegistrations });
  }

  /**
   * Permet de récupérer la liste des inscriptions d'une compétition
   * @returns {Promise<Array<CfcRegistrationItem>>} - Liste des inscriptions
   */
  async fetchCfcRegistrations() {
    var response = await fetch(`/api/cfc-registration/search?cfcTournamentId=${this.state.id}`, {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  /**
   * Permet de récupérer la liste des catégories d'une compétition
   * @returns {Promise<Array<CfcCategoryItem>>} - Liste des catégories
   */
  async fetchCfcCategories() {
    var response = await fetch(`/api/cfc-category/search?cfcTournamentId=${this.state.id}`, {
      headers: {
        Authorization: "gestiaweb"
      }
    });

    return await response.json();
  }

  /**
   * Lorsqu'on change la valeur du select, on filtre les inscriptions en fonction de la catégorie sélectionnée
   * @param {React.ChangeEvent<any>} event - Évenement associé au changement de valeur dans le select
   */
  onChangeOnSelect = event => {
    const cfcCategoryId = event.currentTarget.value ? parseInt(event.currentTarget.value, 10) : null;

    this.setState(
      {
        search: {
          ...this.state.search,
          cfcCategoryId
        }
      },
      () => this.refreshList()
    );
  };

  /**
   * Lorsqu'on saisit une valeur dans le champ de recherche, on filtre les inscriptions en fonction de cette valeur
   * @param {React.ChangeEvent<any>} event - Évenement associé au changement de valeur dans le champ de recherche
   */
  onChangeOnSearch = event => {
    const searchValue = event.currentTarget.value.toLowerCase();

    if (searchValue) {
      this.setState({
        search: {
          ...this.state.search,
          searchValue
        }
      });
    }

    this.refreshList();
  };

  /**
   * Permet de filtrer la liste des inscriptions en fonction de la catégorie sélectionnée et de la valeur saisie dans le champ de recherche
   */
  refreshList() {
    let filteredCfcRegistrations = this.state.cfcRegistrations;

    this.setState(
      {
        filteredCfcRegistrations: this.state.cfcRegistrations
      },
      () => {
        filteredCfcRegistrations = this.state.filteredCfcRegistrations.filter(cfcRegistration => {
          const teamName = cfcRegistration.team_name ? cfcRegistration.team_name.toLowerCase() : "";
          const teams = cfcRegistration.teams ? cfcRegistration.teams.toLowerCase() : "";
          const athleteName = `${cfcRegistration.user.firstname} ${cfcRegistration.user.lastname}`.toLowerCase();
          const categoryName = cfcRegistration.cfc_category.name.toLowerCase();
          const boxName = cfcRegistration.user.coordinate?.name ? cfcRegistration.user.coordinate.name.toLowerCase() : "";

          const { cfcCategoryId, searchValue } = this.state.search;

          const matchesCategory = cfcCategoryId ? cfcRegistration.cfcCategoryId === cfcCategoryId : true;
          const matchesSearch = searchValue ? teamName.includes(searchValue) || teams.includes(searchValue) || athleteName.includes(searchValue) || categoryName.includes(searchValue) || boxName.includes(searchValue) : true;
          return matchesCategory && matchesSearch;
        });

        this.setState({
          filteredCfcRegistrations
        });
      }
    );
  }

  render() {
    return (
      <div>
        <div className="flexline flexline--margin flexline--no-mobile">
          <select className="input-group__input input-group__input--with-small" onChange={this.onChangeOnSelect.bind(this)}>
            <option value="" key="all">
              Toutes
            </option>
            {this.state.cfcCategories.map((cfcCategory, index) => (
              <option key={index} value={cfcCategory.id}>
                {cfcCategory.name}
              </option>
            ))}
          </select>
          <input type="text" className="input-group__input input-group__input--with-small" aria-label="Recherche" placeholder="Rechercher..." onChange={this.onChangeOnSearch.bind(this)} />
        </div>

        <div className="space--16"></div>
        <div className="table table--responsive">
          <table className="table__table table__table--td-bordered">
            <thead>
              <tr className="table__tr">
                <th className="table__th table__th--align-left table__th--main">Équipe / Athlète</th>
                <th className="table__th table__th--align-left table__th--main">Catégories</th>
                <th className="table__th table__th--align-left table__th--main">Box / Club</th>
                <th className="table__th table__th--main">Status</th>
              </tr>
            </thead>
            <tbody>
              {this.state.filteredCfcRegistrations.map((cfcRegistration, index) => (
                <tr>
                  <td className="table__td table__td--align-left">
                    <>
                      {cfcRegistration.team_name ? (
                        <div className="flexline flexline--no-mobile flexline--direction-column">
                          <p className="paragraph paragraph--no-margin paragraph--bold-600 paragraph--break-space">Team {cfcRegistration.team_name}</p>
                          <p className="paragraph paragraph--no-margin paragraph--grey-600 paragraph--break-space">{cfcRegistration.teams}</p>
                        </div>
                      ) : (
                        <p className="paragraph paragraph--no-margin paragraph--bold-600">
                          {cfcRegistration.user.firstname} {cfcRegistration.user.lastname}
                        </p>
                      )}
                    </>
                  </td>
                  <td className="table__td table__td--align-left">{cfcRegistration.cfc_category.name}</td>
                  <td className="table__td table__td--align-left">{cfcRegistration.user.coordinate?.name || "-"}</td>
                  <td className="table__td">{cfcRegistration.state ? <div className="badge badge--success">Confirmé</div> : <div className="badge badge--danger">Liste d'attente</div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
}

var cfcRegistrationsNode = document.getElementById("cfc-registrations-front");
if (cfcRegistrationsNode) {
  var root = ReactDOM.createRoot(cfcRegistrationsNode);
  root.render(<CfcRegistrations id={cfcRegistrationsNode.getAttribute("data-id")} />);
}
